import { Formik } from "formik";
import { useEffect, useState } from "react";
import Spin from "../../../components/Spin";
import { Label } from "../../../components/ui/label";
import CustomLabelText from "../../../components/CustomLabelText";
import { Input } from "../../../components/ui/input";
import { API, fileUpload, get } from "../../../config";
import { PhoneInput } from "../../../components/ui/phone-input";
import SearchSelect from "../../../components/SearchSelect";
import { useRoleList } from "../../../lib/OptionsAPI";
import { Loader2 } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { useOrganization } from "../../../lib/context/OrganisationContext";
import { MultiSelect } from "../../../components/SearchMultiSelect";
import OfficeSelect from "../../../components/OfficeSelect";
import { formatOfficeName } from "../../../lib/Misc";
import { AddUserSchema } from "../../../lib/Validation";
import { Switch } from "../../../components/ui/switch";

const AddEditUser = ({
  id,
  toggleShowAddEditUser,
  edit,
  fetchUserData,
  orgId,
}) => {
  const [userData, setUserData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [officeLoading, setOfficeLoading] = useState(false);
  const [officeListing, setOfficeListing] = useState([]);
  const [officeList, setOfficeList] = useState([]);

  const { organizationData } = useOrganization();

  async function fetchData() {
    try {
      setLoading(true);
      const reponse = await get(`${API.USER}/` + id);
      setUserData(reponse?.data?.data);
      let data = reponse?.data?.data;
      if (data?.office?.state_id ? data?.office?.state_id : false) {
        if (data?.office?.suburb_id ? data?.office?.suburb_id : false) {
          setOfficeListing([
            data?.office
              ? {
                  ...data?.office,
                  value: data?.office.id,
                  label: formatOfficeName(
                    data?.office?.street,
                    data?.office?.suburb,
                  ),
                }
              : {},
          ]);
        }
      }
      setLoading(false);
    } catch (e) {
      setUserData(null);
    }
  }

  useEffect(() => {
    if (officeList) {
      let officeTempListing = officeList?.map((item) => ({
        ...item,
        value: item.id,
        label: formatOfficeName(item?.street, item?.suburb),
      }));
      setOfficeListing(officeTempListing || []);
    } else {
      setOfficeListing([]);
    }
  }, [officeList]);

  useEffect(() => {
    if (edit) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [edit]);

  // Role
  const { roleLoading, roleList = [] } = useRoleList();
  const roleListing = roleList ? roleList : [];

  // const orgID = organizationData?.organizationList?.find(
  //   (item) => item?.value === userData?.organization_ids
  // )?.value;

  const handleFormSubmit = async (value) => {
    const formData = new FormData();

    formData.append("first_name", value?.first_name ? value?.first_name : "");
    formData.append("last_name", value?.last_name ? value?.last_name : "");
    formData.append("mobile", value?.mobile ? value?.mobile : "");
    formData.append("email", value?.email ? value?.email : "");
    formData.append("role_id", value?.role_id ? value?.role_id : "");
    formData.append("office_id", value?.office_id ? value?.office_id : "");

    formData.append("is_compliance", value?.is_compliance ? 1 : 0);
    formData.append("is_senior_manager", value?.is_senior_manager ? 1 : 0);

    for (let i = 0; i < value?.organization_id?.length; i++) {
      formData.append(`organization_ids[${i}]`, value?.organization_id[i]);
    }

    if (edit) {
      formData.append("_method", "put");
    }
    try {
      setLoading(true);
      const { data } = await fileUpload(
        `${API.USER}${edit ? `/${id}` : ""}`,
        formData,
      );
      setLoading(false);
      if (data) {
        toggleShowAddEditUser();
        fetchUserData();
      }
      return data?.data;
    } catch (error) {
      setLoading(false);
    }
  };
  return (
    <Formik
      initialValues={{
        organization_id: userData?.organization_ids
          ? userData?.organization_ids
          : [orgId],
        first_name: userData?.first_name,
        last_name: userData?.last_name,
        mobile: userData?.mobile,
        email: userData?.email,
        office_id: userData?.office_id ? userData?.office_id : "",
        role_id: userData?.role_id ? userData?.role_id : "",
        is_compliance: userData?.is_compliance ? true : false,
        is_senior_manager: userData?.is_senior_manager ? true : false,
      }}
      enableReinitialize={true}
      onSubmit={handleFormSubmit}
      validateOnBlur={true}
      validateOnChange={true}
      validationSchema={AddUserSchema}
    >
      {({
        values,
        errors,
        handleChange,
        handleBlur,
        handleSubmit,
        setFieldValue,
        isValid,
      }) => (
        <>
          {loading && <Spin expand={true} fullscreen={true} />}
          <form
            onSubmit={handleSubmit}
            noValidate
            className="entry-section md:m-0 m-5"
          >
            <div className="form-box overflow-y-auto overflow-x-hidden max-h-[80vh] p-4 border rounded-lg">
              <div className="grid gap-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="first_name">
                      <CustomLabelText
                        required={false}
                        labelText="First Name"
                        errorField={errors?.first_name}
                      />
                    </Label>
                    <Input
                      id="first_name"
                      type="text"
                      name="first_name"
                      value={values?.first_name ? values?.first_name : ""}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      autoComplete="off"
                      onKeyPress={(e) => {
                        e.which === 13 && e.preventDefault();
                      }}
                      className={
                        errors?.first_name &&
                        `focus-visible:ring-red-500 border-red-500`
                      }
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="last_name">
                      <CustomLabelText
                        required={true}
                        labelText="Last Name"
                        errorField={errors?.last_name}
                      />
                    </Label>
                    <Input
                      id="last_name"
                      type="text"
                      name="last_name"
                      value={values?.last_name ? values?.last_name : ""}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      autoComplete="off"
                      onKeyPress={(e) => {
                        e.which === 13 && e.preventDefault();
                      }}
                      className={
                        errors?.last_name &&
                        `focus-visible:ring-red-500 border-red-500`
                      }
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="email">
                      <CustomLabelText
                        required={true}
                        labelText="Email"
                        errorField={errors?.email}
                      />
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="m@example.com"
                      required
                      autoComplete="off"
                      name="email"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values?.email}
                      className={
                        errors?.email &&
                        `focus-visible:ring-red-500 border-red-500`
                      }
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="phone">
                      <CustomLabelText
                        required={false}
                        labelText="Mobile No."
                        errorField={errors?.mobile}
                      />
                    </Label>
                    <PhoneInput
                      onChange={(e) => {
                        setFieldValue("mobile", e);
                      }}
                      value={values?.mobile}
                      defaultValue={"+61"}
                      defaultCountry={"AU"}
                      minLength={15}
                      maxLength={15}
                      onBlur={handleBlur}
                      className={
                        errors?.mobile &&
                        `focus-visible:ring-red-500 border-red-500`
                      }
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="organization_id">
                      <CustomLabelText
                        required={true}
                        labelText="Organization"
                        errorField={errors?.organization_id}
                      />
                    </Label>
                    <div className="relative">
                      <MultiSelect
                        options={
                          organizationData?.organizationList?.length > 0
                            ? organizationData?.organizationList
                            : []
                        }
                        placeholder={"Organization"}
                        onValueChange={(e) => {
                          setFieldValue("organization_id", e);
                          if (e?.length === 0) {
                            setFieldValue("office_id", "");
                          }
                        }}
                        handleBlur={handleBlur}
                        className={
                          errors?.organization_id &&
                          `focus-visible:ring-red-500 border-red-500`
                        }
                        variant="inverted"
                        animation={2}
                        maxCount={2}
                        defaultValue={values?.organization_id}
                      />
                    </div>
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="role_id">
                      <CustomLabelText
                        required={true}
                        labelText="Role"
                        errorField={errors?.role_id}
                      />
                    </Label>

                    <div className="relative">
                      <SearchSelect
                        frameworks={roleListing}
                        optionType={"Role"}
                        handleSelect={(e) => {
                          setFieldValue("role_id", e);
                        }}
                        handleBlur={handleBlur}
                        className={
                          errors?.role_id &&
                          `focus-visible:ring-red-500 border-red-500`
                        }
                        disabled={false}
                        initialValue={values?.role_id}
                      />
                      {roleLoading && (
                        <span className="absolute top-2 right-2 animate-spin">
                          <Loader2 width={20} height={20} />
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="office_id">
                      <CustomLabelText
                        required={false}
                        labelText="Office"
                        errorField={errors?.office_id}
                      />
                    </Label>
                    <div className="relative">
                      <OfficeSelect
                        org_id={values?.organization_id} // Pass the selected state ID
                        frameworkData={officeListing} // List of offices
                        optionType="Office"
                        handleSelect={(e) => {
                          setFieldValue("office_id", e);
                        }}
                        name="office_id"
                        handleBlur={handleBlur}
                        className={
                          errors?.office_id &&
                          `focus-visible:ring-red-500 border-red-500`
                        }
                        disabled={values?.organization_id?.length === 0}
                        initialValue={values?.office_id}
                        setOfficeList={setOfficeList} // Update suburb list as new data loads
                        setOfficeLoading={setOfficeLoading} // Control loading state
                      />
                      {officeLoading && (
                        <span className="absolute top-2 right-2 animate-spin">
                          <Loader2 width={20} height={20} />
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="grid gap-2 grid-cols-2 xl:grid-cols-4">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium">
                        Compliance Manager
                      </span>
                      <Switch
                        checked={values?.is_compliance}
                        onCheckedChange={(e) =>
                          setFieldValue("is_compliance", e)
                        }
                        className="data-[state=checked]:bg-webPrimary"
                      />
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium">
                        Senior Manager
                      </span>
                      <Switch
                        checked={values?.is_senior_manager}
                        onCheckedChange={(e) =>
                          setFieldValue("is_senior_manager", e)
                        }
                        className="data-[state=checked]:bg-webPrimary"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer pt-4 border-t mt-4 flex items-center justify-end">
              <Button type="submit" disabled={!isValid}>
                Save
              </Button>
              <Button
                className="reset ml-3"
                data-dismiss="modal"
                aria-label="Close"
                onClick={(e) => {
                  e.preventDefault();
                  toggleShowAddEditUser();
                }}
                variant="secondary"
              >
                Cancel
              </Button>
            </div>
          </form>
        </>
      )}
    </Formik>
  );
};

export default AddEditUser;
